// pages/EncuestasPreguntas.js
import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Switch, Select, Space,
  Typography, message, Tag, Tooltip, Row, Col, Popconfirm,
  Badge, Statistic, InputNumber
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined,
  ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined,
  QuestionCircleOutlined, FileTextOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Text } = Typography;
const { Option } = Select;

const EncuestasPreguntas = () => {
  const [preguntas, setPreguntas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentPregunta, setCurrentPregunta] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  const columns = [
    {
      title: 'ID',
      dataIndex: 'Id',
      key: 'Id',
      width: 70,
      sorter: (a, b) => a.Id - b.Id,
      sortOrder: sortedInfo.columnKey === 'Id' && sortedInfo.order,
      render: (id) => <Tag color="default">#{id}</Tag>,
      fixed: 'left',
    },
    {
      title: 'Categoría',
      dataIndex: 'CodigoCategoria',
      key: 'CodigoCategoria',
      width: 200,
      sorter: (a, b) => (a.CodigoCategoria || '').localeCompare(b.CodigoCategoria || ''),
      sortOrder: sortedInfo.columnKey === 'CodigoCategoria' && sortedInfo.order,
      render: (cod, record) => (
        <Tooltip title={record.CategoriaDescripcion}>
          <Tag color="purple" style={{ cursor: 'help' }}>
            {cod} - {record.CategoriaDescripcion}
          </Tag>
        </Tooltip>
      ),
      filters: categorias.map(c => ({ text: c.Descripcion, value: c.Codigo })),
      filteredValue: filteredInfo.CodigoCategoria || null,
      onFilter: (value, record) => record.CodigoCategoria === value,
    },
    {
      title: 'Pregunta',
      dataIndex: 'Texto',
      key: 'Texto',
      width: 400,
      sorter: (a, b) => (a.Texto || '').localeCompare(b.Texto || ''),
      sortOrder: sortedInfo.columnKey === 'Texto' && sortedInfo.order,
      render: (t) => <Text strong>{t}</Text>,
    },
    {
      title: 'Permite texto',
      dataIndex: 'PermiteTexto',
      key: 'PermiteTexto',
      width: 120,
      render: (p) => p
        ? <Badge status="success" text="Sí" />
        : <Badge status="default" text="No" />,
      filters: [{ text: 'Sí', value: true }, { text: 'No', value: false }],
      filteredValue: filteredInfo.PermiteTexto || null,
      onFilter: (value, record) => record.PermiteTexto === value,
    },
    {
      title: 'Orden',
      dataIndex: 'Orden',
      key: 'Orden',
      width: 90,
      sorter: (a, b) => (a.Orden || 0) - (b.Orden || 0),
      sortOrder: sortedInfo.columnKey === 'Orden' && sortedInfo.order,
      render: (o) => <Tag color="geekblue">{o}</Tag>,
    },
    {
      title: 'Activo',
      dataIndex: 'Activo',
      key: 'Activo',
      width: 90,
      render: (a) => a
        ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 18 }} />
        : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />,
      filters: [{ text: 'Sí', value: true }, { text: 'No', value: false }],
      filteredValue: filteredInfo.Activo || null,
      onFilter: (value, record) => record.Activo === value,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button type="primary" icon={<EditOutlined />} size="small"
              onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar pregunta?"
            onConfirm={() => handleDelete(record.Id)}
            okText="Sí" cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button danger icon={<DeleteOutlined />} size="small" />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchPreguntas();
    fetchCategorias();
  }, []);

  const fetchPreguntas = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryParams = {
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'Orden',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC')
      };
      if (filteredInfo.CodigoCategoria) {
        queryParams.categoria = filteredInfo.CodigoCategoria[0];
      }
      
      const response = await axios.get(`${BASE_URL}/api/encuestas/preguntas`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });
      
      setPreguntas(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando preguntas:', error);
      message.error('Error al cargar las preguntas');
      setPreguntas([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const fetchCategorias = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/encuestas/categorias`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { activo: true }
      });
      setCategorias(response.data.data || []);
    } catch (error) {
      console.error('❌ Error cargando categorías:', error);
    }
  };

  const handleCreate = () => {
    setModalType('create');
    setCurrentPregunta(null);
    form.resetFields();
    form.setFieldsValue({ Activo: true, PermiteTexto: false, Orden: 0 });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentPregunta(record);
    form.setFieldsValue({
      CodigoCategoria: record.CodigoCategoria,
      Texto: record.Texto,
      PermiteTexto: record.PermiteTexto,
      Orden: record.Orden,
      Activo: record.Activo,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BASE_URL}/api/encuestas/preguntas/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreguntas(preguntas.filter(p => p.Id !== id));
      message.success('Pregunta eliminada correctamente');
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      if (modalType === 'create') {
        await axios.post(`${BASE_URL}/api/encuestas/preguntas`, values, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Pregunta creada correctamente');
      } else {
        await axios.put(`${BASE_URL}/api/encuestas/preguntas/${currentPregunta.Id}`, values, {
          headers: { Authorization: `Bearer ${token}` }
        });
        message.success('Pregunta actualizada correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchPreguntas();
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  const handleSearch = () => {
    setSearchLoading(true);
    fetchPreguntas();
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    fetchPreguntas();
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input
          placeholder="Buscar por texto o categoría..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
          prefix={<SearchOutlined />}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}
          loading={searchLoading}>Buscar</Button>
        <Button icon={<ReloadOutlined />} onClick={handleResetSearch}>Restablecer</Button>
      </Space>
    </div>
  );

  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={8}>
        <Statistic title="Total Preguntas" value={preguntas.length}
          prefix={<QuestionCircleOutlined />} valueStyle={{ color: '#1890ff' }} />
      </Col>
      <Col span={8}>
        <Statistic title="Activas" value={preguntas.filter(p => p.Activo).length}
          suffix={`/ ${preguntas.length}`} prefix={<CheckCircleOutlined />}
          valueStyle={{ color: '#52c41a' }} />
      </Col>
      <Col span={8}>
        <Statistic title="Con texto libre"
          value={preguntas.filter(p => p.PermiteTexto).length}
          prefix={<FileTextOutlined />} valueStyle={{ color: '#722ed1' }} />
      </Col>
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Preguntas de Encuestas"
        subtitle={`${preguntas.length} preguntas registradas`}
        icon={<QuestionCircleOutlined />}
        loading={loading}
        onRefresh={() => fetchPreguntas()}
        onCreate={handleCreate}
        badgeCount={preguntas.length}
        badgeStatus="processing"
        cardStyle={{ borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
      >
        {estadisticas}
        {searchBar}
        <Table
          columns={columns}
          dataSource={preguntas}
          rowKey="Id"
          loading={loading}
          scroll={{ x: 1300, y: 600 }}
          size="middle"
          pagination={{ pageSize: 10, showSizeChanger: true }}
          onChange={(pagination, filters, sorter) => {
            setFilteredInfo(filters);
            setSortedInfo(sorter);
          }}
          locale={{ emptyText: 'No se encontraron preguntas' }}
        />
      </PageContainer>

      <Modal
        title={modalType === 'create' ? 'Nueva pregunta' : 'Editar pregunta'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => { setModalVisible(false); form.resetFields(); }}
        width={700}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 40 }}
      >
        <Form form={form} layout="vertical"
          initialValues={{ Activo: true, PermiteTexto: false, Orden: 0 }}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="CodigoCategoria" label="Categoría"
                rules={[{ required: true, message: 'Seleccione la categoría' }]}>
                <Select placeholder="Seleccione categoría" showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }>
                  {categorias.map(c => (
                    <Option key={c.Codigo} value={c.Codigo}>
                      {c.Codigo} - {c.Descripcion}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="Orden" label="Orden">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="Texto" label="Texto de la pregunta"
            rules={[{ required: true, message: 'Ingrese el texto', max: 255 }]}>
            <Input.TextArea rows={2} maxLength={255} showCount
              placeholder="Ej: ¿Cómo valoraría la limpieza de su habitación?" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="PermiteTexto" label="Permite comentario de texto"
                valuePropName="checked"
                tooltip="Si se activa, el cliente podrá añadir un comentario">
                <Switch checkedChildren="Sí" unCheckedChildren="No" style={{ minWidth: 80 }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="Activo" label="Activo" valuePropName="checked">
                <Switch checkedChildren="Sí" unCheckedChildren="No" style={{ minWidth: 80 }} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default EncuestasPreguntas;